import { useState } from "react";
import { motion } from "framer-motion";
import { Beaker, Droplets, Factory, ArrowRight } from "lucide-react";

const IMG_PURITY =
  "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/6k98katd_Screenshot%202026-07-18%20at%2009.26.55.png";
const IMG_DIALYSIS =
  "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/nv508xsj_Screenshot%202026-07-18%20at%2009.27.15.png";
const IMG_ENFOQUE_1 =
  "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/625q4e7i_Screenshot%202026-07-18%20at%2009.27.36.png";
const IMG_ENFOQUE_2 =
  "https://customer-assets.emergentagent.com/job_clami-fillers-chile/artifacts/5elvki3u_Screenshot%202026-07-18%20at%2009.27.23.png";

const ENFOQUES = [
  {
    id: "enfoque-1",
    label: "Enfoque 1",
    title: "Materia prima e instalación",
    body:
      "Selección de HA de grado medicinal bajo estándares EP/USP/KP. Instalación coreana KGMP con controles de temperatura y humedad clase ISO. Estabilización previa al reticulado.",
    img: IMG_ENFOQUE_1,
  },
  {
    id: "enfoque-2",
    label: "Enfoque 2",
    title: "Reticulado, diálisis y autoclave",
    body:
      "Cross-linking con BDDE a densidad controlada. 8 diálisis en 12 días para eliminación de BDDE libre. Mezcla, envasado en jeringa 1.0 mL y esterilización por autoclave con enfriamiento rápido.",
    img: IMG_ENFOQUE_2,
  },
];

export default function Process() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = ENFOQUES[activeIdx];

  return (
    <section id="proceso" data-testid="process-section" className="relative py-20 md:py-28">
      <div className="max-w-[1160px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <span className="chip mb-5">
            <span className="dot" /> Ingeniería · KGMP · ISO 13485
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-[56px] font-bold text-[#0B0B14] tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Del ingrediente hasta la jeringa. Trazable, controlado, coreano.
          </h2>
          <p className="mt-6 text-[#3A3A48] max-w-2xl mx-auto leading-relaxed">
            AEGENBIO Co., Ltd. fabrica Clamignon bajo un proceso de 3 enfoques, con
            controles químicos, mecánicos y térmicos que puedes auditar en la ficha
            técnica.
          </p>
        </div>

        {/* SUB 1 — Materia prima */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="process-purity"
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center mb-6 md:mb-8"
        >
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-full bg-[#F1EDFF] flex items-center justify-center">
                <Beaker className="w-4 h-4 text-[#5A48C9]" strokeWidth={1.7} />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9]">
                01 · Materia prima
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0B0B14] tracking-tight mb-4">
              HA de grado medicinal — rango estricto.
            </h3>
            <p className="text-[#3A3A48] leading-relaxed">
              Selección de ácido hialurónico bajo estándares farmacéuticos internacionales{" "}
              <span className="text-[#0B0B14] font-semibold">EP / USP / KP</span>. Alta
              pureza, mínima carga de endotoxinas y homogeneidad de peso molecular por
              lote — todo antes de siquiera comenzar el reticulado.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#1E1E2C]">
              <Bullet>Grado medicinal HA, no cosmético</Bullet>
              <Bullet>Trazabilidad por certificado de análisis</Bullet>
              <Bullet>Control de peso molecular por lote</Bullet>
            </ul>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="card-soft p-4 md:p-6 overflow-hidden">
              <img
                src={IMG_PURITY}
                alt="Rango estricto de materia prima HA — Estándares químicos EP/USP/KP"
                loading="lazy"
                className="w-full h-auto rounded-2xl object-contain bg-white"
              />
            </div>
          </div>
        </motion.div>

        {/* SUB 2 — Diálisis */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="process-dialysis"
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center mb-6 md:mb-8"
        >
          <div className="lg:col-span-7 order-1">
            <div className="card-soft p-4 md:p-6 overflow-hidden">
              <img
                src={IMG_DIALYSIS}
                alt="Diálisis 12 días — Membrana semipermeable / PBS · Eliminación de BDDE"
                loading="lazy"
                className="w-full h-auto rounded-2xl object-contain bg-white"
              />
            </div>
          </div>
          <div className="lg:col-span-5 order-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-full bg-[#F1EDFF] flex items-center justify-center">
                <Droplets className="w-4 h-4 text-[#5A48C9]" strokeWidth={1.7} />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9]">
                02 · Purificación
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0B0B14] tracking-tight mb-4">
              8 diálisis. 12 días. BDDE libre eliminado.
            </h3>
            <p className="text-[#3A3A48] leading-relaxed">
              Membrana semipermeable en PBS. El BDDE residual se difunde de alta a baja
              concentración durante 12 días, hasta niveles indetectables. Resultado:
              osmolaridad{" "}
              <span className="text-[#0B0B14] font-semibold">~300 mOsm/kg</span>{" "}
              (isotónico fisiológico), menor edema post-inyección y menor deformación
              química del gel.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#1E1E2C]">
              <Bullet>Membrana semipermeable en solución PBS</Bullet>
              <Bullet>Eliminación progresiva días 1 → 8 → 12</Bullet>
              <Bullet>Purificación auditable por espectrofotometría</Bullet>
            </ul>
          </div>
        </motion.div>

        {/* SUB 3 — Proceso de fabricación con tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="process-manufacturing"
          className="card-glass p-6 md:p-10 mt-4 md:mt-6"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-9 h-9 rounded-full bg-white/70 border border-black/[0.06] flex items-center justify-center">
                  <Factory className="w-4 h-4 text-[#5A48C9]" strokeWidth={1.7} />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9]">
                  03 · Manufactura completa
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0B0B14] tracking-tight leading-tight max-w-xl">
                Proceso de fabricación en 3 enfoques.
              </h3>
            </div>

            {/* Tabs */}
            <div
              role="tablist"
              className="inline-flex p-1 rounded-full bg-white/80 backdrop-blur border border-black/[0.06]"
            >
              {ENFOQUES.map((e, i) => (
                <button
                  key={e.id}
                  role="tab"
                  aria-selected={activeIdx === i}
                  data-testid={`process-tab-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`btn-pill text-sm px-4 py-2 transition-all ${
                    activeIdx === i
                      ? "bg-gradient-to-b from-[#8B7CFF] to-[#7C6AF5] text-white shadow-[0_6px_18px_-6px_rgba(124,106,245,0.5)]"
                      : "bg-transparent text-[#1E1E2C] hover:bg-white"
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8 order-2 lg:order-1">
              <div className="rounded-2xl bg-white p-4 md:p-6 border border-black/[0.06] overflow-hidden">
                <motion.img
                  key={active.id}
                  initial={{ opacity: 0.2, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  src={active.img}
                  alt={`${active.label} — ${active.title}`}
                  loading="lazy"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div className="lg:col-span-4 order-1 lg:order-2">
              <motion.div
                key={active.id + "-copy"}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                data-testid={`process-panel-${activeIdx}`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9] mb-3">
                  {active.label}
                </p>
                <h4 className="font-display text-xl md:text-2xl font-bold text-[#0B0B14] tracking-tight mb-4">
                  {active.title}
                </h4>
                <p className="text-[#3A3A48] leading-relaxed">{active.body}</p>
                <a
                  href="#contacto"
                  data-testid="process-cta"
                  className="btn-pill btn-ghost mt-6 inline-flex"
                >
                  Solicitar ficha técnica
                  <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#7C6AF5] flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}
